import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import ArrowButton from '@/components/atoms/ArrowButton/ArrowButton';
import { useCarouselImpressions, useCarouselTabbing } from '@/components/molecules/Carousel/Carousel.hooks';
import { content } from '@/helpers/content';
import { dataLayerPush } from '@/helpers/dataLayer';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import 'swiper/css';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { createCards, getWrapAroundDistance } from './Carousel.utils';
const CarouselProps = ({ items, onSlideRender, onSlideClassName, children, initialSlide, minimumSlides, hideSlideOverflow = true, hideSlidesUntilHydrated = false, className, options = {}, loop = true, labels, onAdditionalDataLayerProps = () => ({}), onSlideImpressions = () => { }, focusedLastSlide, }) => {
    const [activeIndex, setActiveIndex] = useState(initialSlide || 0);
    const ref = useRef();
    const [realSlidesPerView, setRealSlidesPerView] = useState(1);
    const activeItem = items[activeIndex % items.length];
    useEffect(() => {
        if (initialSlide === undefined || items.length === 1) {
            // If there is only one item, then there is never a need to scroll to a slide.
            return;
        }
        ref.current?.slideReset(0);
        ref.current?.slideToLoop(initialSlide, 0);
    }, [initialSlide, ref]);
    // After Swiper is ready and DOM is painted, get the real slidesPerView
    useEffect(() => {
        if (!ref.current)
            return;
        // Swiper sets slidesPerView after init and after resize
        const updateSlidesPerView = () => {
            let spv = 1;
            // Use Swiper DOM to count visible slides
            const count = ref.current?.slidesEl?.querySelectorAll('.swiper-slide-visible').length;
            if (count && count > 0) {
                spv = count;
            }
            else if (typeof ref.current?.params.slidesPerView === 'number') {
                spv = ref.current.params.slidesPerView;
            }
            else if (typeof options.slidesPerView === 'number') {
                spv = options.slidesPerView;
            }
            else if (typeof options.slidesPerView === 'string' && options.slidesPerView !== 'auto') {
                spv = parseInt(options.slidesPerView, 10) || 1;
            }
            else if (typeof options.slidesPerView === 'object' && options.slidesPerView !== null) {
                // Find the largest matching breakpoint
                const breakpoints = Object.keys(options.slidesPerView)
                    .map(Number)
                    .sort((a, b) => b - a);
                for (const bp of breakpoints) {
                    if (window.innerWidth >= bp) {
                        spv = options.slidesPerView[bp];
                        break;
                    }
                }
            }
            setRealSlidesPerView(spv);
        };
        updateSlidesPerView();
        // Listen for Swiper's resize and update events
        ref.current.on('resize', updateSlidesPerView);
        ref.current.on('update', updateSlidesPerView);
        return () => {
            ref.current?.off('resize', updateSlidesPerView);
            ref.current?.off('update', updateSlidesPerView);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current, options.slidesPerView]);
    const { setSwiperReady, activeTabbingIndex } = useCarouselTabbing(ref, items.length, loop);
    // Only add extra slides for looping carousels
    const loopAdditionalSlides = 4;
    const desiredSlides = loop ? (minimumSlides ?? 0) + loopAdditionalSlides * 2 : items.length;
    const cards = createCards(items, desiredSlides);
    useCarouselImpressions(ref, cards, onSlideImpressions);
    const filtersDataLayerPush = (interaction, index) => {
        const activeItem = items[index % items.length];
        const activeItemBrand = activeItem && 'brand' in activeItem ? activeItem.brand : undefined;
        dataLayerPush('carousel', {
            carousel_interaction: interaction,
            carousel_progress: index % items.length,
            brand_name: activeItemBrand,
            ...onAdditionalDataLayerProps(activeItem),
        });
    };
    // Used for DataLayer events; since these fire before the carousel index updates
    const getNextIndex = () => (cards.length - 1 === activeIndex ? 0 : activeIndex + 1);
    const getPrevIndex = () => (activeIndex === 0 ? cards.length - 1 : activeIndex - 1);
    const handleIndicator = () => {
        filtersDataLayerPush('Indicator', getNextIndex());
        ref.current?.slideNext();
    };
    const handleNextBtn = () => {
        filtersDataLayerPush('Next', getNextIndex());
        ref.current?.slideNext();
    };
    const handlePrevBtn = () => {
        filtersDataLayerPush('Previous', getPrevIndex());
        ref.current?.slidePrev();
    };
    const slides = (_jsx(Swiper, { ...options, modules: [Navigation, Pagination, ...(options.modules ?? [])], loop: loop, loopAdditionalSlides: loop ? loopAdditionalSlides : undefined, watchSlidesProgress: true, touchEventsTarget: "container", className: clsx('relative w-full', hideSlideOverflow ? '!overflow-hidden' : '!overflow-visible', className), maxBackfaceHiddenSlides: 0, slidesPerView: options.slidesPerView ?? 'auto', onBeforeInit: (swiper) => {
            ref.current = swiper;
        }, onInit: () => setSwiperReady(), onActiveIndexChange: (swiper) => setActiveIndex(swiper.realIndex), onTouchEnd: (swiper, event) => {
            options.onTouchEnd?.(swiper, event);
            const swipeDirection = swiper.swipeDirection;
            if (swipeDirection === 'prev') {
                filtersDataLayerPush('Previous', getPrevIndex());
            }
            else if (swipeDirection === 'next') {
                filtersDataLayerPush('Next', getNextIndex());
            }
        }, "data-show-after-hydration": hideSlidesUntilHydrated ? true : undefined, children: cards.map(({ item, key }, index) => {
            const isActive = index === activeTabbingIndex;
            const isAriaHidden = index > items.length - 1;
            const meta = {
                item,
                isActive,
                positionRelativeToActiveSlide: getWrapAroundDistance(activeIndex, index, cards.length),
                index,
                activeIndex,
                accessibilityProps: {
                    tabIndex: isActive ? 0 : -1,
                    'aria-hidden': isAriaHidden,
                },
                swiperRef: ref.current,
            };
            return (_jsx(SwiperSlide, { "data-key": key, className: onSlideClassName?.(meta), "aria-hidden": isAriaHidden, children: onSlideRender(meta) }, key));
        }) }));
    const paginationDots = Math.min(6, loop ? items.length : items.length - realSlidesPerView + 1);
    const pagination = (_jsxs(_Fragment, { children: [_jsx("div", { "aria-live": "polite", className: "sr-only", children: content(labels.slideProgressLabel, (activeIndex % items.length) + 1, items.length) }), _jsx("button", { className: "-mx-0.25 flex disabled:pointer-events-none disabled:opacity-50", onClick: handleIndicator, "data-testid": "pagination-dots", "aria-hidden": true, tabIndex: -1, children: Array.from({ length: paginationDots }).map((_, index) => {
                    const relativeActiveIndex = activeIndex % items.length;
                    let isActive = relativeActiveIndex === index;
                    if (loop && items.length > 6) {
                        isActive =
                            (index < 4 && relativeActiveIndex === index) ||
                                (index === 4 && relativeActiveIndex >= 4 && relativeActiveIndex <= items.length - 2) ||
                                (index === 5 && relativeActiveIndex === items.length - 1);
                    }
                    if (!loop && items.length > 6) {
                        isActive =
                            (index < 4 && relativeActiveIndex === index) ||
                                (index === 4 &&
                                    relativeActiveIndex >= 4 &&
                                    relativeActiveIndex <= items.length - 1 - realSlidesPerView) ||
                                (index === 5 && relativeActiveIndex === items.length - realSlidesPerView);
                    }
                    return (_jsx("div", { className: "block cursor-pointer px-0.25 py-0.75", children: _jsx("div", { className: clsx('h-[5px] w-1.5', isActive
                                ? 'bg-symbol-brand'
                                : 'dark:bg-ui-specific-glass-light-40 light:bg-ui-specific-glass-dark-40') }) }, index));
                }) })] }));
    // Use the real slidesPerView for navigation logic
    const atEnd = !loop && activeIndex >= items.length - realSlidesPerView;
    const atBeginning = !loop && activeIndex === 0;
    const previousButton = (_jsx(ArrowButton, { variant: labels.previousIcon, disabled: atBeginning, children: (props) => (_jsx("button", { ...props, onClick: handlePrevBtn, "aria-label": labels.previousLabel, "data-testid": "prev-button", children: props.icon })) }));
    const nextButton = (_jsx(ArrowButton, { variant: labels.nextIcon, disabled: atEnd || focusedLastSlide, children: (props) => (_jsx("button", { ...props, onClick: handleNextBtn, "aria-label": labels.nextLabel, "data-testid": "next-button", children: props.icon })) }));
    return children({
        slides,
        pagination,
        previousButton,
        nextButton,
        activeItem,
    });
};
export default CarouselProps;
